// Small shared UI primitives in the TPFL house style.
import type { ButtonHTMLAttributes, ReactNode } from "react";

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`bg-surface border border-line rounded-2xl shadow-card ${className}`}>{children}</div>
  );
}

export function Button({
  variant = "primary",
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-xl font-medium text-[15px] px-4 h-11 transition-colors disabled:opacity-50 disabled:pointer-events-none select-none";
  const styles = {
    primary: "bg-navy text-white active:bg-navy-hover",
    secondary: "bg-navy-soft text-navy border border-navy-line active:bg-navy-soft/70",
    ghost: "bg-transparent text-ink-muted active:bg-line-soft",
  }[variant];
  return (
    <button className={`${base} ${styles} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-ink-subtle gap-3">
      <i className="ph ph-circle-notch text-2xl animate-spin" />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function EmptyState({ icon, title, hint }: { icon: string; title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6 gap-2">
      <i className={`ph ${icon} text-4xl text-ink-faint`} />
      <div className="text-ink font-medium">{title}</div>
      {hint && <div className="text-sm text-ink-subtle max-w-xs">{hint}</div>}
    </div>
  );
}

export function ScreenHeader({ title, subtitle, right }: { title: string; subtitle?: string; right?: ReactNode }) {
  return (
    <header className="px-5 pt-4 pb-3 flex items-end justify-between gap-3">
      <div>
        <h1 className="text-[22px] font-bold tracking-tight text-navy">{title}</h1>
        {subtitle && <p className="text-[13px] text-ink-subtle mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </header>
  );
}

export function Money({ cents }: { cents?: number }) {
  if (cents == null) return null;
  return <>${(cents / 100).toFixed(2)}</>;
}
